import PropTypes from 'prop-types';
import React from 'react';
import CoreImage from '../../../variables/images';

const Image = ({
  alt = '',
  className = 'image',
  decorative = false,
  id = 'logoBig',
  loading = 'lazy',
  role = null,
  sizes = null,
  src = null,
  srcSet = null,
  usePicture = false,
  ...props
}) => {
  // If src is provided, use it directly (external image)
  // Otherwise, use the id to get internal image from CoreImage
  const imageSrc = src || CoreImage.getImage(id);

  // Determine if image is decorative
  // An image is decorative if:
  // 1. explicitly marked as decorative, OR
  // 2. no alt text provided AND no role provided (implicitly decorative)
  const isDecorative = decorative || (alt === '' && !role);

  // Handle alt text for accessibility
  let altText = '';
  if (isDecorative) {
    // Decorative images should have empty alt text
    altText = '';
  } else if (alt) {
    // Use provided alt text
    altText = alt;
  } else {
    // Generate alt text from ID for informative images
    altText = `Image: ${id}`;
  }

  // Base image props
  const imageProps = {
    className,
    src: imageSrc,
    alt: altText,
    loading: loading || 'lazy',
    ...(sizes && { sizes }),
    ...(srcSet && { srcSet }),
    ...(role && { role }),
    ...(isDecorative && { 'aria-hidden': 'true' }),
    ...props,
  };

  // Use picture element for responsive images with multiple sources
  if (usePicture && srcSet) {
    return (
      <picture className={className}>
        <source srcSet={srcSet} sizes={sizes} />
        <img {...imageProps} />
      </picture>
    );
  }

  // Use regular img element
  return <img {...imageProps} />;
};

Image.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.string,
  decorative: PropTypes.bool, // For decorative images (no alt needed)
  id: PropTypes.oneOf([
    'logoBig',
    'logoIconS',
    'noBooks',
    'noItems',
    'noUsers',
    'noQuestions',
    'noCourses',
    'barMenuLogo',
    'logoWhite',
    'noPictureProgram',
    'cover-card-task',
    'cover-card-out-of-time',
    'cover-card-quizz',
    'noMembers',
    'videoContentPreview',
    'login',
    'signup1',
    'signup2',
    'signup3',
    'signup4',
    'signup5',
    'cover-test-started',
    'cover-test-blocked',
    'cover-test-default',
    'cover-test-confirmation',
    'cover-test-formative',
    'cover-test-formative-late',
    'image-layout-progress',
    'cover-card-videolesson',
    'cover-card-audio',
    'cover-card-doc',
    'cover-card-link',
    'cover-card-video',
    'noMaterials',
    'noUnitProgram',
    'noModuleCover',
  ]),
  loading: PropTypes.oneOf(['lazy', 'eager']),
  role: PropTypes.string, // Custom ARIA role
  sizes: PropTypes.string, // For responsive images
  src: PropTypes.string, // External image URL
  srcSet: PropTypes.string, // For responsive images
  usePicture: PropTypes.bool, // Use picture element for responsive images
};

export default Image;
